import {
  DiagnosticAxes,
  GradingReport,
  QuestionAnalysis,
  QuestionEvaluation,
  ReviewFlag,
  StudentDiagnosticReport,
} from '../types';
import {
  clampConfidence,
  computeOverallConfidence,
  computeReportConfidence,
  mergeReviewFlags,
  normalizeRubricLevel,
  requiresTeacherReview,
} from './evaluationPolicy';

const formatDiagnosticAxes = (diagnostics: DiagnosticAxes): string =>
  `C:${diagnostics.conceptAccuracy} K:${diagnostics.conceptCompleteness} R:${diagnostics.logicalCoherence} E:${diagnostics.scientificExpression} T:${diagnostics.representationApplication}`;

const formatReviewFlags = (reviewFlags: ReviewFlag[]): string =>
  reviewFlags.map((flag) => `${flag.type}: ${flag.message}`).join(' | ');

export const toLegacyQuestionAnalysis = (
  questionEvaluation: QuestionEvaluation,
): QuestionAnalysis => {
  const critiqueSections = [
    `What you did well: ${questionEvaluation.feedback.whatYouDidWell}`,
    `What to improve: ${questionEvaluation.feedback.whatToImprove}`,
    `Try this next time: ${questionEvaluation.feedback.tryThisNextTime}`,
  ];

  const logicSections = [
    `Score reason: ${questionEvaluation.scoring.scoreReason}`,
    `Rubric level rules: ${questionEvaluation.rubricAlignment.levelMarkRules.map((rule) => `${rule.level}=${rule.allowedMarks.join('|')} (${rule.evidenceRequirement})`).join('; ') || 'No level mark rules extracted.'}`,
    `Rubric level rule applied: ${questionEvaluation.scoring.rubricLevelRuleApplied || 'Not specified.'}`,
    `Scoring evidence IDs: ${questionEvaluation.scoring.scoringEvidenceIds.join('; ') || 'None'}`,
    `Rubric criteria: ${questionEvaluation.rubricAlignment.criteria.map((criterion) => `${criterion.criterionId}=${criterion.judgment}`).join('; ') || 'None'}`,
    `Criterion scores: ${questionEvaluation.scoring.criterionScores.map((criterionScore) => `${criterionScore.criterionId}=${criterionScore.awardedMarks}/${criterionScore.maxMarks}`).join('; ') || 'None'}`,
    `Observed evidence: ${questionEvaluation.evidence.observableEvidence.join('; ') || 'No explicit evidence recorded.'}`,
    `Matched key points: ${questionEvaluation.evidence.matchedKeyPoints.join('; ') || 'None'}`,
    `Missing key points: ${questionEvaluation.evidence.missingKeyPoints.join('; ') || 'None'}`,
    `Diagnostics: ${formatDiagnosticAxes(questionEvaluation.diagnostics)}`,
    `Confidence: ${(questionEvaluation.overallConfidence * 100).toFixed(0)}%`,
    `Teacher view: ${questionEvaluation.teacherView.conciseExplanation}`,
    `Reliability: ${questionEvaluation.reliability.reason}`,
  ];

  if (questionEvaluation.reviewFlags.length > 0) {
    logicSections.push(`Review flags: ${formatReviewFlags(questionEvaluation.reviewFlags)}`);
  }

  if (!questionEvaluation.scoreAlignment.passed) {
    logicSections.push(`Score alignment issues: ${questionEvaluation.scoreAlignment.issues.join(' | ')}`);
  }

  return {
    questionNumber: questionEvaluation.questionNumber,
    questionText: questionEvaluation.questionText,
    maxPoints: questionEvaluation.scoring.maxMarks,
    awardedPoints: questionEvaluation.scoring.awardedMarks,
    rubricLevel: questionEvaluation.scoring.rubricLevel,
    studentAnswerSummary: questionEvaluation.transcription.rawAnswer || '[No answer detected]',
    critique: critiqueSections.join('\n'),
    evaluationLogic: logicSections.join('\n'),
    rubricReference:
      questionEvaluation.scoring.rubricLevelRuleApplied ||
      questionEvaluation.scoring.partialCreditRuleApplied ||
      questionEvaluation.evidence.rubricCriteriaMet.join('; ') ||
      'Rubric-based evaluation.',
    spellingErrors: questionEvaluation.transcription.spellingObservations,
  };
};

export const toLegacyGradingReport = (
  report: StudentDiagnosticReport,
): GradingReport => ({
  studentName: report.studentName,
  gradeLevel: report.gradeLevel,
  subject: report.subject,
  totalScore: report.totalScore,
  maxTotalScore: report.maxTotalScore,
  percentage: report.percentage,
  overallFeedback: [
    report.studentSummary.strengths[0],
    report.studentSummary.areasForGrowth[0],
    report.studentSummary.nextLearningSteps[0],
  ]
    .filter(Boolean)
    .join(' '),
  strengths: report.studentSummary.strengths,
  areasForImprovement: report.studentSummary.areasForGrowth,
  questionAnalysis: report.questionEvaluations.map(toLegacyQuestionAnalysis),
  pagesUsed: report.pagesUsed,
  segregationReasoning: report.groupingReasoning,
});

const defaultDiagnostics: DiagnosticAxes = {
  conceptAccuracy: 0,
  conceptCompleteness: 0,
  logicalCoherence: 0,
  scientificExpression: 0,
  representationApplication: 0,
};

export const fromLegacyGradingReport = (
  report: GradingReport,
): StudentDiagnosticReport => {
  const questionEvaluations: QuestionEvaluation[] = report.questionAnalysis.map((questionAnalysis) => {
    const reviewFlags: ReviewFlag[] = [];
    if (questionAnalysis.studentAnswerSummary.includes('[unclear]')) {
      reviewFlags.push({
        type: 'low_transcription_confidence',
        severity: 'medium',
        message: 'The transcribed answer includes unclear text.',
      });
    }

    const transcriptionConfidence = questionAnalysis.studentAnswerSummary.includes('[unclear]')
      ? 0.65
      : 0.9;
    const formatMatchConfidence = 0.9;
    const scoringConfidence = 0.85;

    return {
      questionNumber: questionAnalysis.questionNumber,
      questionType: 'short_constructed',
      questionText: questionAnalysis.questionText,
      expectedAnswerFormat: 'Unknown',
      formatMatchConfidence,
      transcription: {
        questionNumber: questionAnalysis.questionNumber,
        rawAnswer: questionAnalysis.studentAnswerSummary,
        normalizedAnswer: questionAnalysis.studentAnswerSummary,
        answerDetected: questionAnalysis.studentAnswerSummary.trim().length > 0,
        questionNumberSeen: questionAnalysis.questionNumber,
        handwritingNotes: [],
        transcriptionConfidence,
        unclearSpans: questionAnalysis.studentAnswerSummary.includes('[unclear]') ? ['[unclear]'] : [],
        spellingObservations: questionAnalysis.spellingErrors,
        evidenceSnippets: questionAnalysis.studentAnswerSummary
          ? [
              {
                id: `q${questionAnalysis.questionNumber}-raw-answer-1`,
                quote: questionAnalysis.studentAnswerSummary,
                source: 'raw_answer',
              },
            ]
          : [],
        uncertaintyFlags: questionAnalysis.studentAnswerSummary.includes('[unclear]')
          ? [
              {
                type: 'illegible_text',
                message: 'The transcribed answer includes unclear text.',
                evidenceSnippetIds: [`q${questionAnalysis.questionNumber}-raw-answer-1`],
              },
            ]
          : [],
      },
      diagnostics: defaultDiagnostics,
      rubricAlignment: {
        questionNumber: questionAnalysis.questionNumber,
        questionType: 'short_constructed',
        questionText: questionAnalysis.questionText,
        expectedAnswerFormat: 'Unknown',
        formatMatchConfidence,
        diagnostics: defaultDiagnostics,
        levelMarkRules: [],
        criteria: [
          {
            criterionId: `q${questionAnalysis.questionNumber}-legacy-criterion-1`,
            criterionText: questionAnalysis.rubricReference || 'Legacy rubric reference',
            judgment: questionAnalysis.awardedPoints > 0 ? 'partial' : 'unmet',
            supportingEvidenceIds: questionAnalysis.studentAnswerSummary
              ? [`q${questionAnalysis.questionNumber}-raw-answer-1`]
              : [],
            notes: 'Migrated from legacy grading report.',
          },
        ],
        matchedKeyPoints: [],
        missingKeyPoints: [],
        contradictoryStatements: [],
        supportedClaims: [],
        unsupportedClaims: [],
        misconceptions: [],
      },
      evidence: {
        matchedKeyPoints: [],
        missingKeyPoints: [],
        contradictoryStatements: [],
        rubricCriteriaMet: questionAnalysis.rubricReference ? [questionAnalysis.rubricReference] : [],
        rubricCriteriaMissed: [],
        observableEvidence: questionAnalysis.studentAnswerSummary
          ? [`"${questionAnalysis.studentAnswerSummary}"`]
          : [],
      },
      scoring: {
        awardedMarks: questionAnalysis.awardedPoints,
        maxMarks: questionAnalysis.maxPoints,
        rubricLevel: normalizeRubricLevel(
          questionAnalysis.rubricLevel,
          questionAnalysis.awardedPoints,
          questionAnalysis.maxPoints,
        ),
        scoreReason: questionAnalysis.evaluationLogic || 'Migrated from legacy grading report.',
        idealAnswerExact: '',
        idealAnswerStudentFriendly: '',
        rubricLevelRuleApplied: undefined,
        partialCreditRuleApplied: questionAnalysis.rubricReference,
        scoringMethod: 'constrained_ai',
        scoringConfidence,
        scoringEvidenceIds: questionAnalysis.studentAnswerSummary
          ? [`q${questionAnalysis.questionNumber}-raw-answer-1`]
          : [],
        criterionScores: [
          {
            criterionId: `q${questionAnalysis.questionNumber}-legacy-criterion-1`,
            awardedMarks: questionAnalysis.awardedPoints,
            maxMarks: questionAnalysis.maxPoints,
            rationale: questionAnalysis.evaluationLogic || 'Migrated from legacy scoring.',
          },
        ],
      },
      scoreAlignment: {
        passed: true,
        issues: [],
      },
      feedback: {
        whatYouDidWell: questionAnalysis.critique || 'You attempted the question.',
        whatToImprove: questionAnalysis.rubricReference || 'Review the rubric-aligned answer.',
        tryThisNextTime: 'Check the model answer and write the science idea clearly.',
        feedbackConfidence: 0.75,
        evidenceIds: questionAnalysis.studentAnswerSummary
          ? [`q${questionAnalysis.questionNumber}-raw-answer-1`]
          : [],
        criterionIds: [`q${questionAnalysis.questionNumber}-legacy-criterion-1`],
        unsupportedStatements: [],
      },
      misconceptions: [],
      reviewFlags,
      reliability: {
        confidenceLevel: transcriptionConfidence >= 0.85 ? 'high' : 'medium',
        confidenceScore: computeOverallConfidence(
          transcriptionConfidence,
          formatMatchConfidence,
          scoringConfidence,
        ),
        reviewRequired: reviewFlags.length > 0,
        reason:
          reviewFlags.length > 0
            ? reviewFlags.map((flag) => flag.message).join(' ')
            : 'Migrated from legacy grading report.',
        checks: [],
      },
      teacherView: {
        conciseExplanation: questionAnalysis.evaluationLogic || 'Migrated from legacy grading report.',
        keyEvidence: questionAnalysis.studentAnswerSummary
          ? [questionAnalysis.studentAnswerSummary]
          : [],
        nextStep: 'Review the rubric and revise the answer using direct evidence.',
      },
      overallConfidence: computeOverallConfidence(
        transcriptionConfidence,
        formatMatchConfidence,
        scoringConfidence,
      ),
    };
  });

  const teacherSummaryFlags = mergeReviewFlags(
    questionEvaluations.flatMap((questionEvaluation) => questionEvaluation.reviewFlags),
  );
  const totalScore = report.totalScore;
  const maxTotalScore = report.maxTotalScore;
  const percentage = maxTotalScore > 0
    ? Math.round((totalScore / maxTotalScore) * 100)
    : report.percentage;

  return {
    studentName: report.studentName,
    gradeLevel: report.gradeLevel,
    subject: report.subject,
    pagesUsed: report.pagesUsed,
    groupingReasoning: report.segregationReasoning || 'Migrated from legacy report.',
    teacherReviewRequired: requiresTeacherReview(questionEvaluations),
    reportConfidence: clampConfidence(computeReportConfidence(questionEvaluations)),
    reviewState: requiresTeacherReview(questionEvaluations) ? 'needs_review' : 'generated',
    totalScore,
    maxTotalScore,
    percentage,
    studentSummary: {
      strengths: report.strengths,
      areasForGrowth: report.areasForImprovement,
      nextLearningSteps: report.areasForImprovement.length > 0
        ? [`Focus next on: ${report.areasForImprovement[0]}`]
        : ['Continue practicing with the same careful approach.'],
    },
    teacherSummary: {
      diagnosticOverview: report.overallFeedback,
      keyMisconceptions: [],
      reviewFlags: teacherSummaryFlags,
    },
    questionEvaluations,
  };
};
